import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { clsx } from "clsx";

import type { PaymentMethodFormErrors } from "../../hooks/use-payment-method-form-validation.ts";
import {
	fieldErrorBorderClass,
	FieldErrorMessage,
} from "../field-error/field-error.tsx";
import styles from "./cc-form-inputs.module.css";

export const CcFormInputs = ({
	newPaymentValues,
	handleSetNewPaymentValue,
	errors,
}: {
	newPaymentValues: {
		cardholderName: string;
		// cardNumber, expirationDate, and cvv are managed securely by Braintree Hosted Fields
	};
	handleSetNewPaymentValue: (field: string, value: string) => void;
	errors: PaymentMethodFormErrors;
}) => {
	const cardNumberError = errors.cardNumberMissing || errors.cardNumberInvalid;
	const expirationDateError =
		errors.expirationDateMissing || errors.expirationDateInvalid;
	const cvvError = errors.cvvMissing || errors.cvvInvalid;

	return (
		<>
			<div className={styles.field}>
				<Label>
					Cardholder Name <span className={"required"}>&nbsp;*</span>
				</Label>
				<Input
					className={errors.cardholderName ? fieldErrorBorderClass : undefined}
					onChange={(e) =>
						handleSetNewPaymentValue("cardholderName", e.target.value as string)
					}
					value={newPaymentValues.cardholderName}
				/>
				{errors.cardholderName ? <FieldErrorMessage /> : null}
			</div>
			<div className={styles.field}>
				<Label>
					Card Number <span className={"required"}>&nbsp;*</span>
				</Label>
				{/* Braintree will inject a secure iframe into this container.
				    This keeps sensitive data out of the React state and PCI scope. */}
				<div
					className={clsx(
						styles.hostedField,
						cardNumberError && styles.hostedFieldError,
					)}
					id={"card-number"}
				/>
				{errors.cardNumberMissing ? <FieldErrorMessage /> : null}
				{errors.cardNumberInvalid ? (
					<FieldErrorMessage>Invalid card number</FieldErrorMessage>
				) : null}
			</div>
			<div className={styles.fieldRow}>
				<div className={styles.fieldRowItem}>
					<Label>
						Expiration Date <span className={"required"}>&nbsp;*</span>
					</Label>
					{/* Braintree secure container for expiration date (MM/YY) */}
					<div
						className={clsx(
							styles.hostedField,
							expirationDateError && styles.hostedFieldError,
						)}
						id={"expiration-date"}
					/>
					{errors.expirationDateMissing ? <FieldErrorMessage /> : null}
					{errors.expirationDateInvalid ? (
						<FieldErrorMessage>Invalid expiration date</FieldErrorMessage>
					) : null}
				</div>
				<div className={styles.fieldRowItem}>
					<Label>
						CVV <span className={"required"}>&nbsp;*</span>
					</Label>
					{/* Braintree secure container for the security code */}
					<div
						className={clsx(
							styles.hostedField,
							cvvError && styles.hostedFieldError,
						)}
						id={"cvv"}
					/>
					{errors.cvvMissing ? <FieldErrorMessage /> : null}
					{errors.cvvInvalid ? (
						<FieldErrorMessage>Invalid CVV</FieldErrorMessage>
					) : null}
				</div>
			</div>
		</>
	);
};
